import React, { useState, useEffect } from 'react';
import { AlignLeft, Calendar, Check, Copy, Hash, Link2, Loader2, Mail, Phone, RefreshCw, ToggleLeft, Type } from 'lucide-react';
import { CustomFieldsSkeleton } from '../ui/SkeletonLoader';
import { PropSelectRow, PropTextRow, PropToggleRow, ValueRow } from '../shared/property-rows';
import { useApp } from '../AppProvider';
import { useAuth } from '../../lib/auth';
import { supabase } from '../../lib/supabase';

interface CustomFieldDefinition {
  id: string;
  organization_id: string;
  field_name: string;
  field_label: string;
  field_type: 'text' | 'number' | 'date' | 'boolean' | 'select' | 'textarea' | 'email' | 'phone' | 'url';
  field_options?: string[];
  is_required: boolean;
  default_value?: string;
  help_text?: string;
  sort_order: number;
}

interface CustomFieldValue {
  id?: string;
  field_definition_id: string;
  field_value: string;
}

interface CustomFieldsSectionProps {
  clientId: string;
  isEditing?: boolean;
  onSave?: () => void;
  loading?: boolean;
}

const ICONE: Record<string, React.ReactNode> = {
  text: <Type className="h-3.5 w-3.5" />,
  textarea: <AlignLeft className="h-3.5 w-3.5" />,
  number: <Hash className="h-3.5 w-3.5" />,
  date: <Calendar className="h-3.5 w-3.5" />,
  boolean: <ToggleLeft className="h-3.5 w-3.5" />,
  select: <AlignLeft className="h-3.5 w-3.5" />,
  email: <Mail className="h-3.5 w-3.5" />,
  phone: <Phone className="h-3.5 w-3.5" />,
  url: <Link2 className="h-3.5 w-3.5" />,
};

const iconaDi = (tipo: string) => (
  <span className="text-[#787774] dark:text-gray-500">{ICONE[tipo] ?? ICONE.text}</span>
);

export function CustomFieldsSection({ clientId, isEditing = false, onSave, loading: parentLoading = false }: CustomFieldsSectionProps) {
  const { toast } = useApp();
  const { user } = useAuth();
  const [fields, setFields] = useState<CustomFieldDefinition[]>([]);
  const [values, setValues] = useState<Record<string, CustomFieldValue>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [localIsEditing, setLocalIsEditing] = useState(false);

  useEffect(() => {
    fetchOrganizationId();
  }, [user]);

  useEffect(() => {
    if (organizationId) {
      fetchFieldsAndValues();
    }
  }, [clientId, organizationId]);

  const fetchOrganizationId = async () => {
    if (!user) return;
    
    try {
      const { data: userProfile, error } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      setOrganizationId(userProfile.organization_id);
    } catch (error) {
      console.error('Error fetching organization ID:', error);
      toast.error('Errore nel caricamento dell\'organizzazione');
    }
  };

  const fetchFieldsAndValues = async () => {
    if (!organizationId) return;
    
    try {
      setLoading(true);

      // Fetch field definitions for this organization
      const { data: fieldsData, error: fieldsError } = await supabase
        .from('custom_field_definitions')
        .select('*')
        .eq('organization_id', organizationId)
        .order('sort_order', { ascending: true });

      if (fieldsError) throw fieldsError;

      // Fetch existing values for this client
      const { data: valuesData, error: valuesError } = await supabase
        .from('client_custom_fields')
        .select('*')
        .eq('client_id', clientId);

      if (valuesError) throw valuesError;

      setFields(fieldsData || []);

      // Create values map
      const valuesMap: Record<string, CustomFieldValue> = {};
      
      fieldsData?.forEach(field => {
        const existingValue = valuesData?.find(v => v.field_definition_id === field.id);
        valuesMap[field.id] = {
          id: existingValue?.id,
          field_definition_id: field.id,
          field_value: existingValue?.field_value || field.default_value || ''
        };
      });

      setValues(valuesMap);
    } catch (error) {
      console.error('Error fetching custom fields:', error);
      toast.error('Errore nel caricamento dei campi personalizzati');
    } finally {
      setLoading(false);
    }
  };

  const handleValueChange = (fieldId: string, value: string) => {
    setValues(prev => ({
      ...prev,
      [fieldId]: {
        ...prev[fieldId],
        field_value: value
      }
    }));
  };

  const validateFields = () => {
    const errors: string[] = [];

    fields.forEach(field => {
      const value = values[field.id]?.field_value || '';
      
      if (field.is_required && !value.trim()) {
        errors.push(`Il campo "${field.field_label}" è obbligatorio`);
      }

      if (value && field.field_type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        errors.push(`Il campo "${field.field_label}" deve contenere un email valido`);
      }

      if (value && field.field_type === 'url' && !/^https?:\/\/.+/.test(value)) {
        errors.push(`Il campo "${field.field_label}" deve contenere un URL valido`);
      }

      if (value && field.field_type === 'number' && isNaN(Number(value))) {
        errors.push(`Il campo "${field.field_label}" deve contenere un numero valido`);
      }
    });

    if (errors.length > 0) {
      toast.error(errors[0]);
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateFields()) return;

    try {
      setSaving(true);

      // Separate new records from updates
      const newRecords = Object.values(values)
        .filter(value => value.field_value.trim() && !value.id) // New records without ID
        .map(value => ({
          organization_id: organizationId,
          client_id: clientId,
          field_definition_id: value.field_definition_id,
          field_value: value.field_value,
          updated_at: new Date().toISOString()
        }));

      const updateRecords = Object.values(values)
        .filter(value => value.field_value.trim() && value.id) // Existing records with ID
        .map(value => ({
          id: value.id,
          organization_id: organizationId,
          client_id: clientId,
          field_definition_id: value.field_definition_id,
          field_value: value.field_value,
          updated_at: new Date().toISOString()
        }));

      // Delete existing values that are now empty
      const emptyValues = Object.values(values)
        .filter(value => !value.field_value.trim() && value.id)
        .map(value => value.id);

      if (emptyValues.length > 0) {
        const { error: deleteError } = await supabase
          .from('client_custom_fields')
          .delete()
          .in('id', emptyValues);

        if (deleteError) throw deleteError;
      }

      // Insert new records
      if (newRecords.length > 0) {
        const { error: insertError } = await supabase
          .from('client_custom_fields')
          .insert(newRecords);

        if (insertError) throw insertError;
      }

      // Update existing records
      if (updateRecords.length > 0) {
        for (const record of updateRecords) {
          const { error: updateError } = await supabase
            .from('client_custom_fields')
            .update({
              field_value: record.field_value,
              updated_at: record.updated_at
            })
            .eq('id', record.id);

          if (updateError) throw updateError;
        }
      }

      toast.success('Campi personalizzati salvati con successo');
      
      if (onSave) {
        onSave();
      }

      // Refresh data
      await fetchFieldsAndValues();
      
    } catch (error) {
      console.error('Error saving custom fields:', error);
      toast.error('Errore durante il salvataggio dei campi personalizzati');
    } finally {
      setSaving(false);
    }
  };

  if (loading || parentLoading) {
    return <CustomFieldsSkeleton />;
  }

  if (fields.length === 0) {
    return null;
  }

  const handleCopy = (value: string, label: string) => {
    navigator.clipboard.writeText(value).then(() => {
      toast.success(`${label} copiato negli appunti`);
    });
  };

  const renderReadOnlyValue = (field: CustomFieldDefinition) => {
    const value = values[field.id]?.field_value || '';

    if (!value.trim()) {
      return (
        <span className="text-sm text-gray-400 dark:text-gray-500 italic">
          Nessun valore
        </span>
      );
    }

    switch (field.field_type) {
      case 'boolean':
        return (
          <div className="flex items-center space-x-2">
            <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
              value === 'true' ? 'bg-green-500/20 border-green-400' : 'bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600'
            }`}>
              {value === 'true' && (
                <div className="w-2 h-2 bg-green-400 rounded-sm"></div>
              )}
            </div>
            <span className="text-sm text-[#37352F] dark:text-white">
              {value === 'true' ? 'Sì' : 'No'}
            </span>
          </div>
        );

      case 'email':
        return (
          <span className="text-sm text-blue-400 hover:text-blue-300 cursor-pointer"
                onClick={() => window.open(`mailto:${value}`, '_blank')}>
            {value}
          </span>
        );

      case 'phone':
        return (
          <span className="text-sm text-green-400 hover:text-green-300 cursor-pointer"
                onClick={() => window.open(`tel:${value}`, '_blank')}>
            {value}
          </span>
        );

      case 'url':
        return (
          <span className="text-sm text-blue-400 hover:text-blue-300 cursor-pointer underline"
                onClick={() => window.open(value.startsWith('http') ? value : `https://${value}`, '_blank')}>
            {value}
          </span>
        );

      case 'date':
        const formattedDate = new Date(value).toLocaleDateString('it-IT');
        return (
          <span className="text-sm text-[#37352F] dark:text-white">{formattedDate}</span>
        );

      default:
        return (
          <span className="text-sm text-[#37352F] dark:text-white">{value}</span>
        );
    }
  };

  const handleEditToggle = () => {
    setLocalIsEditing(!localIsEditing);
  };

  const handleSaveAndClose = async () => {
    const success = await handleSave();
    if (success) {
      setLocalIsEditing(false);
    }
  };

  return (
    /* Stesso linguaggio del resto del pannello (righe etichetta-valore, azioni discrete):
       prima era una scheda bianca con ombra, icona in un riquadro, titolo grande e bottoni
       pieni, fuori posto dentro "Altri campi" (segnalazione Luca, 18/09/2026). */
    <div>
      <div className="flex items-center justify-end gap-1 pb-1">
        <button
          type="button"
          onClick={fetchFieldsAndValues}
          disabled={loading}
          title="Aggiorna campi"
          className="rounded p-1 text-[#787774] hover:bg-[rgba(55,53,47,0.08)] dark:text-gray-400 dark:hover:bg-white/[0.06] transition-colors"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
        {localIsEditing ? (
          <>
            <button
              type="button"
              onClick={() => setLocalIsEditing(false)}
              disabled={saving}
              className="rounded px-1.5 py-1 text-[11.5px] text-[#787774] hover:bg-[rgba(55,53,47,0.08)] dark:text-gray-400 dark:hover:bg-white/[0.06] transition-colors disabled:opacity-50"
            >
              Annulla
            </button>
            <button
              type="button"
              onClick={handleSaveAndClose}
              disabled={saving}
              className="flex items-center gap-1 rounded px-1.5 py-1 text-[11.5px] font-medium text-[#337EA9] dark:text-[#529CCA] hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/[0.06] transition-colors disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
              Salva
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={handleEditToggle}
            className="rounded px-1.5 py-1 text-[11.5px] text-[#787774] hover:bg-[rgba(55,53,47,0.08)] dark:text-gray-400 dark:hover:bg-white/[0.06] transition-colors"
          >
            Modifica
          </button>
        )}
      </div>

      <div>
        {fields.map((field) => {
          const valore = values[field.id]?.field_value || '';
          if (localIsEditing) {
            if (field.field_type === 'boolean') {
              return (
                <PropToggleRow
                  key={field.id}
                  icon={iconaDi(field.field_type)}
                  label={field.field_label}
                  checked={valore === 'true'}
                  onChange={(v) => handleValueChange(field.id, v ? 'true' : 'false')}
                  onLabel="Sì"
                  offLabel="No"
                />
              );
            }
            if (field.field_type === 'select') {
              return (
                <PropSelectRow
                  key={field.id}
                  icon={iconaDi(field.field_type)}
                  label={field.field_label}
                  required={field.is_required}
                  value={valore}
                  onChange={(v) => handleValueChange(field.id, v)}
                  options={(field.field_options || []).map((o) => ({ value: o, label: o }))}
                  placeholder={field.help_text || 'Vuoto'}
                />
              );
            }
            if (field.field_type === 'textarea') {
              return (
                <div key={field.id} className="px-1.5 py-1">
                  <label className="mb-1 block text-[11.5px] text-[#787774] dark:text-gray-400">{field.field_label}</label>
                  <textarea
                    value={valore}
                    onChange={(e) => handleValueChange(field.id, e.target.value)}
                    rows={3}
                    placeholder={field.help_text || 'Vuoto'}
                    className="w-full resize-y rounded border border-[rgba(55,53,47,0.16)] bg-transparent p-2 text-[13px] text-[#37352F] placeholder-[rgba(55,53,47,0.4)] focus:border-[#2383E2] focus:outline-none dark:border-gray-600 dark:text-white dark:placeholder-gray-500"
                  />
                </div>
              );
            }
            const tipoInput =
              field.field_type === 'phone' ? 'tel'
              : ['number', 'date', 'email', 'url'].includes(field.field_type) ? field.field_type
              : 'text';
            return (
              <PropTextRow
                key={field.id}
                icon={iconaDi(field.field_type)}
                label={field.field_label}
                required={field.is_required}
                value={valore}
                onChange={(v) => handleValueChange(field.id, v)}
                placeholder={field.help_text || 'Vuoto'}
                type={tipoInput}
              />
            );
          }
          return (
            <ValueRow
              key={field.id}
              icon={iconaDi(field.field_type)}
              label={field.field_label}
              actions={
                valore ? (
                  <button
                    type="button"
                    onClick={() => handleCopy(valore, field.field_label)}
                    title="Copia"
                    className="rounded p-1 text-[#787774] hover:bg-[rgba(55,53,47,0.08)] dark:text-gray-400 dark:hover:bg-white/[0.06]"
                  >
                    <Copy className="h-3 w-3" />
                  </button>
                ) : undefined
              }
            >
              {valore ? renderReadOnlyValue(field) : undefined}
            </ValueRow>
          );
        })}
      </div>
    </div>
  );
}
